import React from "react";
import { PortalDomain, PORTAL_BRANDS } from "../../types/branding.types";

export interface WizardLogoProps {
  variant?: PortalDomain;
  size?: "sm" | "md" | "lg";
  className?: string;
  showText?: boolean;
}

/**
 * WizardLogo Component
 *
 * Displays the Olorin wizard mascot logo with domain-specific variants.
 * Each variant represents a different subdomain (main, fraud, streaming, radio, omen).
 *
 * Variants:
 * - main: Olorin.AI (parent portal)
 * - fraud: Olorin.AI Fraud (fraud detection)
 * - streaming: Olorin.ai Media Enrichment (media streaming)
 * - radio: Olorin.ai Media Enrichment (radio management)
 * - omen: Olorin.AI Omen (speech translation)
 *
 * @example
 * <WizardLogo variant="fraud" size="lg" showText />
 */
export const WizardLogo: React.FC<WizardLogoProps> = ({
  variant = "main",
  size = "md",
  className = "",
  showText = true,
}) => {
  const sizeClasses = {
    sm: "h-8",
    md: "h-12",
    lg: "h-16",
  };

  const textSizeClasses = {
    sm: "text-base",
    md: "text-xl",
    lg: "text-2xl",
  };

  const brand = PORTAL_BRANDS[variant];
  const logoSrc = `/logos/wizard/${variant}.png`;

  if (variant === "olorin") {
    const olorinLogoSizes = { sm: "h-8", md: "h-10", lg: "h-14" };
    const olorinTextSizes = { sm: "text-lg", md: "text-xl", lg: "text-3xl" };
    const olorinSloganSizes = {
      sm: "text-[9px]",
      md: "text-[10px]",
      lg: "text-xs",
    };

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <img
          src="/logos/wizard/olorin.png"
          alt="Olorin logo"
          className={`${olorinLogoSizes[size]}`}
          style={{
            filter: "drop-shadow(0 0 8px rgba(191, 95, 255, 0.4))",
            mixBlendMode: "lighten",
            borderRadius: "22%",
          }}
        />
        {showText && (
          <div className="flex flex-col leading-tight">
            <span
              className={`${olorinTextSizes[size]} font-bold tracking-wide font-mono`}
              style={{
                color: "var(--crt-bright, #ffffff)",
                textShadow: "0 0 15px var(--crt-glow, rgba(191, 95, 255, 0.4))",
              }}
            >
              OLORIN
              <span style={{ color: "var(--crt-purple, #BF5FFF)" }}>.ai</span>
            </span>
            <span
              className={`${olorinSloganSizes[size]} font-mono tracking-widest uppercase`}
              style={{ color: "var(--crt-dim, #888888)" }}
            >
              Video you can talk to.
            </span>
          </div>
        )}
      </div>
    );
  }

  if (variant === "bayit") {
    const bayitLogoSizes = { sm: "h-8", md: "h-10", lg: "h-14" };
    const bayitTextSizes = { sm: "text-lg", md: "text-xl", lg: "text-3xl" };
    const bayitSloganSizes = {
      sm: "text-[9px]",
      md: "text-[10px]",
      lg: "text-xs",
    };

    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <img
          src="/assets/bayit-logo.png"
          alt={`${brand.fullName} logo`}
          className={`${bayitLogoSizes[size]}`}
          style={{
            filter: "drop-shadow(0 0 8px rgba(139, 92, 246, 0.4))",
            mixBlendMode: "lighten",
            borderRadius: "22%",
          }}
        />
        {showText && (
          <div className="flex flex-col leading-tight">
            <span
              className={`${bayitTextSizes[size]} font-bold tracking-wide`}
              style={{ color: "var(--crt-bright, #ffffff)" }}
            >
              Bayit
              <span style={{ color: "var(--crt-purple, #BF5FFF)" }}>+</span>
            </span>
            <span
              className={`${bayitSloganSizes[size]} font-mono tracking-widest uppercase`}
              style={{ color: "var(--crt-dim, #888888)" }}
            >
              Your Home. Anywhere.
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Wizard Icon/Logo */}
      <img
        src={logoSrc}
        alt={`${brand.fullName} logo`}
        className={`${sizeClasses[size]} wizard-logo glow-icon`}
        style={{ mixBlendMode: "lighten", borderRadius: "22%" }}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
        }}
      />

      {/* Logo Text */}
      {showText && (
        <span
          className={`${textSizeClasses[size]} wizard-text font-bold tracking-wide`}
        >
          {brand.logoText}
        </span>
      )}
    </div>
  );
};

export default WizardLogo;
